// frontend/src/components/Events/EventDocumentsModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  alpha,
  useTheme,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import { eventsAPI } from '../../services/eventsAPI';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    padding: theme.spacing(2),
  },
  '& .MuiDialog-paper': {
    width: 'min(620px, calc(100vw - 32px))',
    maxWidth: 620,
    minWidth: 0,
    height: 'min(500px, calc(100vh - 32px))',
    maxHeight: 'calc(100vh - 32px)',
    margin: 0,
    overflow: 'hidden',
    borderRadius: theme.spacing(4.5),
    border: `1px solid ${alpha(theme.palette.common.white, 0.82)}`,
    background: `
      linear-gradient(145deg,
        ${alpha(theme.palette.common.white, 0.74)},
        ${alpha(theme.palette.grey[100], 0.46)} 46%,
        ${alpha(theme.palette.primary.main, 0.08)}
      ),
      radial-gradient(circle at 8% 4%, ${alpha(theme.palette.info.light, 0.15)}, transparent 34%),
      radial-gradient(circle at 92% 90%, ${alpha(theme.palette.primary.main, 0.13)}, transparent 42%)
    `,
    backgroundColor: alpha(theme.palette.common.white, 0.56),
    backdropFilter: 'blur(30px) saturate(150%)',
    WebkitBackdropFilter: 'blur(30px) saturate(150%)',
    boxShadow: `0 28px 90px ${alpha(theme.palette.common.black, 0.22)}`,
    zIndex: 1300,
  },
  '& .MuiBackdrop-root': {
    backgroundColor: alpha(theme.palette.common.black, 0.32),
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
  color: theme.palette.text.primary,
  background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.50)}, ${alpha(theme.palette.common.white, 0.14)})`,
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.62)}`,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(2.6, 3),
  background: `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.16)}, ${alpha(theme.palette.primary.main, 0.025)})`,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.common.white, 0.32),
    borderRadius: '999px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.text.secondary, 0.22),
    borderRadius: '999px',
    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.42),
    },
  },
}));

// Область загрузки
const DropzoneArea = styled(Paper)(({ theme, isdragactive }) => ({
  padding: theme.spacing(2.2),
  marginTop: theme.spacing(1.5),
  marginBottom: theme.spacing(2.4),
  textAlign: 'center',
  cursor: 'pointer',
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.52)}, ${alpha(theme.palette.common.white, 0.18)})`,
  backgroundColor: alpha(theme.palette.common.white, 0.30),
  backdropFilter: 'blur(18px) saturate(135%)',
  WebkitBackdropFilter: 'blur(18px) saturate(135%)',
  border: `1px dashed ${isdragactive === 'true'
    ? alpha(theme.palette.primary.main, 0.62)
    : alpha(theme.palette.common.white, 0.82)}`,
  borderRadius: theme.spacing(3),
  transition: 'transform 180ms ease, border-color 180ms ease, background-color 180ms ease, box-shadow 180ms ease',
  width: '100%',
  boxShadow: `0 8px 26px ${alpha(theme.palette.common.black, 0.045)}`,
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.42),
    backgroundColor: alpha(theme.palette.common.white, 0.40),
    boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.065)}`,
  },
}));

// Карточка документа
const DocumentCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  transition: 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 220ms ease, border-color 220ms ease',
  border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
  height: 'auto',
  width: 142,
  maxWidth: 142,
  minHeight: 154,
  display: 'flex',
  boxSizing: 'border-box',
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.56)}, ${alpha(theme.palette.common.white, 0.22)})`,
  backgroundColor: alpha(theme.palette.common.white, 0.30),
  backdropFilter: 'blur(16px) saturate(135%)',
  WebkitBackdropFilter: 'blur(16px) saturate(135%)',
  boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.045)}`,
  flexDirection: 'column',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 14px 34px ${alpha(theme.palette.common.black, 0.085)}`,
    borderColor: alpha(theme.palette.primary.main, 0.34),
  },
}));

// Бейдж удаления в правом верхнем углу
const DeleteBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  zIndex: 20,
  backgroundColor: alpha(theme.palette.common.white, 0.72),
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: `1px solid ${alpha(theme.palette.common.white, 0.84)}`,
  borderRadius: '50%',
  width: 30,
  height: 30,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: `0 5px 14px ${alpha(theme.palette.common.black, 0.08)}`,
  cursor: 'pointer',
  transition: 'transform 180ms ease, background-color 180ms ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.error.main, 0.12),
    transform: 'scale(1.08)',
  },
}));

// Иконка документа
const DocumentIcon = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  margin: `${theme.spacing(1.6)} auto ${theme.spacing(0.9)}`,
  borderRadius: '18px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: alpha(theme.palette.primary.main, 0.075),
  border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
  boxShadow: `0 8px 20px ${alpha(theme.palette.common.black, 0.045)}`,
}));

// Название документа
const DocumentName = styled(Typography)({
  fontWeight: 600,
  fontSize: '0.78rem',
  textAlign: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  lineHeight: 1.3,
  minHeight: 32,
  padding: '0 10px',
});

const getFileIcon = (fileName) => {
  if (!fileName) return <FileIcon sx={{ fontSize: 30, color: '#78909c' }} />;

  const ext = fileName.split('.').pop()?.toLowerCase();

  if (ext === 'pdf') return <PdfIcon sx={{ fontSize: 30, color: '#ef5350' }} />;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <ImageIcon sx={{ fontSize: 30, color: '#66bb6a' }} />;
  if (['doc', 'docx'].includes(ext)) return <DescriptionIcon sx={{ fontSize: 30, color: '#42a5f5' }} />;
  if (['xls', 'xlsx'].includes(ext)) return <DescriptionIcon sx={{ fontSize: 30, color: '#66bb6a' }} />;
  if (['ppt', 'pptx'].includes(ext)) return <DescriptionIcon sx={{ fontSize: 30, color: '#ffa726' }} />;

  return <FileIcon sx={{ fontSize: 30, color: '#78909c' }} />;
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const getFullFileUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/media')) return `https://fincollab.ru${path}`;
  return `https://fincollab.ru/media/${path}`;
};

const getFileName = (doc) => {
  if (!doc) return 'Документ';
  return doc.name || doc.filename || 'Документ';
};

const getDocumentId = (doc, index) => {
  if (!doc) return `doc-${index}`;
  return doc.id || `doc-${index}`;
};

const getDocumentUrl = (doc) => {
  if (!doc) return null;
  // 🔥 ИСПРАВЛЕНО: отбрасываем blob-ссылки, используем только реальные URL
  if (doc.url && !doc.url.startsWith('blob:')) {
    return doc.url;
  }
  return null;
};

const getDocumentSize = (doc) => {
  if (!doc) return 0;
  return doc.size || 0;
};

const getDocumentDate = (doc) => {
  if (!doc) return null;
  return doc.uploaded_at || doc.created_at || null;
};

const EventDocumentsModal = ({
  open,
  onClose,
  eventId,
  documents = [],
  canManageDocuments = false,
  onDocumentsUpdate
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [localDocuments, setLocalDocuments] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      // 🔥 ИСПРАВЛЕНО: фильтруем документы, убираем blob-ссылки
      const validDocs = Array.isArray(documents)
        ? documents.filter(doc =>
            doc !== null &&
            doc !== undefined &&
            doc.url &&
            !doc.url.startsWith('blob:')  // 👈 Игнорируем blob-ссылки
          )
        : [];
      console.log('📄 Загружены документы:', validDocs);
      setLocalDocuments(validDocs);
      setError('');
      setSuccess(false);
    }
  }, [open, documents]);

  const onDrop = async (acceptedFiles) => {
    console.log('🟢 onDrop вызван! Файлы:', acceptedFiles);
    if (!canManageDocuments) return;

    setUploading(true);
    setError('');

    try {
      const uploadedDocs = [];

      for (let i = 0; i < acceptedFiles.length; i++) {
        const file = acceptedFiles[i];
        const formData = new FormData();
        formData.append('document', file);

        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));

        const response = await eventsAPI.uploadEventDocument(eventId, formData, (progress) => {
          setUploadProgress(prev => ({ ...prev, [file.name]: progress }));
        });

        console.log('✅ Ответ после загрузки документа:', response);

        if (response && response.url && !response.url.startsWith('blob:')) {
          uploadedDocs.push({
            id: response.id || Date.now() + i,
            name: file.name,
            filename: response.filename || file.name,
            size: file.size,
            url: response.url,
            uploaded_at: new Date().toISOString()
          });
        }

        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[file.name];
          return newProgress;
        });
      }

      const newDocuments = [...localDocuments, ...uploadedDocs];
      console.log('📦 Новый список документов:', newDocuments);
      setLocalDocuments(newDocuments);

      if (onDocumentsUpdate) {
        onDocumentsUpdate(newDocuments);
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);

    } catch (err) {
      console.error('❌ Ошибка загрузки:', err);
      setError('Не удалось загрузить файлы');
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: !canManageDocuments || uploading,
    maxSize: 10 * 1024 * 1024,
  });

  // 🔥 ИСПРАВЛЕННАЯ ФУНКЦИЯ СКАЧИВАНИЯ (как в ProjectDocuments.js)
  const handleDownload = (url, filename) => {
    if (!url) {
      console.error('❌ Нет URL для скачивания');
      return;
    }

    // Создаем временную ссылку для скачивания
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (documentId, event) => {
    if (!canManageDocuments || !documentId) return;

    event.stopPropagation();

    if (!window.confirm('Удалить этот документ?')) return;

    setLoading(true);
    try {
      await eventsAPI.deleteEventDocument(eventId, documentId);

      const newDocuments = localDocuments.filter(doc => doc && doc.id !== documentId);
      setLocalDocuments(newDocuments);

      if (onDocumentsUpdate) {
        onDocumentsUpdate(newDocuments);
      }

    } catch (err) {
      console.error('❌ Ошибка удаления:', err);
      setError('Не удалось удалить документ');
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '50%',
              color: 'primary.main',
              backgroundColor: alpha(theme.palette.primary.main, 0.10),
              border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
              boxShadow: `0 7px 18px ${alpha(theme.palette.common.black, 0.05)}`,
            }}
          >
            <FolderIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.15rem', sm: '1.3rem' },
                lineHeight: 1.2,
              }}
            >
              Документы мероприятия
            </Typography>
            <Typography sx={{ mt: 0.3, color: 'text.secondary', fontSize: '0.78rem' }}>
              Материалы и файлы мероприятия
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            width: 38,
            height: 38,
            color: 'text.secondary',
            backgroundColor: alpha(theme.palette.common.white, 0.42),
            border: `1px solid ${alpha(theme.palette.common.white, 0.78)}`,
            boxShadow: `0 6px 18px ${alpha(theme.palette.common.black, 0.05)}`,
            '&:hover': {
              color: 'text.primary',
              backgroundColor: alpha(theme.palette.common.white, 0.70),
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        {success && (
          <Alert severity="success" sx={{
            mb: 2,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
            background: `linear-gradient(145deg, ${alpha(theme.palette.success.light, 0.22)}, ${alpha(theme.palette.common.white, 0.34)})`,
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
          }}>
            Документ успешно загружен
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{
            mb: 2,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.error.main, 0.18)}`,
            background: `linear-gradient(145deg, ${alpha(theme.palette.error.light, 0.18)}, ${alpha(theme.palette.common.white, 0.34)})`,
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
          }}>
            {error}
          </Alert>
        )}

        {/* Зона загрузки - только для тех, у кого есть права */}
        {canManageDocuments && (
          <DropzoneArea
            {...getRootProps()}
            isdragactive={isDragActive ? 'true' : 'false'}
          >
            <input {...getInputProps()} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: '50%',
                  color: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, isDragActive ? 0.14 : 0.08),
                  border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
                }}
              >
                <UploadIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box sx={{ textAlign: 'left' }}>
                <Typography sx={{ fontWeight: 650, fontSize: '0.92rem' }}>
                  {isDragActive ? 'Отпустите файлы' : 'Загрузить документы'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem', mt: 0.25 }}>
                  Перетащите файлы или кликните для выбора · максимум 10 MB
                </Typography>
              </Box>
            </Box>
          </DropzoneArea>
        )}

        {/* Прогресс загрузки */}
        {Object.keys(uploadProgress).length > 0 && (
          <Box
            sx={{
              mb: 2.4,
              p: 1.5,
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.common.white, 0.30),
              border: `1px solid ${alpha(theme.palette.common.white, 0.66)}`,
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
            }}
          >
            {Object.entries(uploadProgress).map(([fileName, progress]) => (
              <Box key={fileName} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                    {fileName}
                  </Typography>
                  <Typography variant="body2">{progress}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3
                    }
                  }}
                />
              </Box>
            ))}
          </Box>
        )}

        {/* Сетка документов */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2.2, mb: 1.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '0.98rem',
              color: 'text.primary',
            }}
          >
            Загруженные документы
          </Typography>
          <Box
            sx={{
              px: 1.15,
              py: 0.45,
              borderRadius: 999,
              fontSize: '0.72rem',
              fontWeight: 700,
              color: 'text.secondary',
              backgroundColor: alpha(theme.palette.common.white, 0.42),
              border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
            }}
          >
            {localDocuments.length}
          </Box>
        </Box>

        {localDocuments.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              px: 2,
              borderRadius: 3,
              border: `1px dashed ${alpha(theme.palette.common.white, 0.72)}`,
              backgroundColor: alpha(theme.palette.common.white, 0.20),
            }}
          >
            <Box
              sx={{
                width: 58,
                height: 58,
                mx: 'auto',
                mb: 1.5,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '50%',
                color: 'text.disabled',
                backgroundColor: alpha(theme.palette.common.white, 0.38),
                border: `1px solid ${alpha(theme.palette.common.white, 0.68)}`,
              }}
            >
              <FolderIcon sx={{ fontSize: 30, opacity: 0.65 }} />
            </Box>
            <Typography color="text.secondary" sx={{ fontSize: '0.88rem' }}>
              Нет загруженных документов
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {localDocuments.map((doc, index) => {
              const docId = getDocumentId(doc, index);
              const fileName = getFileName(doc);
              const fileUrl = getDocumentUrl(doc);

              return (
                <Grid item key={docId}>
                  <DocumentCard
                    onClick={() => handleDownload(fileUrl, fileName)}
                    sx={{ position: 'relative' }}
                  >
                    {/* Бейдж удаления - только для тех, у кого есть права */}
                    {canManageDocuments && doc.id && (
                      <DeleteBadge onClick={(e) => handleDelete(doc.id, e)}>
                        <Tooltip title="Удалить">
                          <DeleteIcon
                            sx={{
                              fontSize: 18,
                              color: theme.palette.error.main
                            }}
                          />
                        </Tooltip>
                      </DeleteBadge>
                    )}

                    <DocumentIcon>
                      {getFileIcon(fileName)}
                    </DocumentIcon>
                    <CardContent sx={{ p: 1, pt: 0, pb: 1.2 }}>
                      <DocumentName>
                        {fileName}
                      </DocumentName>
                      {getDocumentSize(doc) > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.55,
                            textAlign: 'center',
                            color: 'text.secondary',
                            fontSize: '0.68rem',
                          }}
                        >
                          {formatFileSize(getDocumentSize(doc))}
                        </Typography>
                      )}
                    </CardContent>
                  </DocumentCard>
                </Grid>
              );
            })}
          </Grid>
        )}
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default EventDocumentsModal;